import { useEffect, useRef } from 'react';
import { init, use, type ECharts, type ComposeOption } from 'echarts/core';
import { SankeyChart, type SankeySeriesOption } from 'echarts/charts';
import { TooltipComponent, type TooltipComponentOption } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import type { CashFlow } from '../api.js';
import { useThemeMode } from '../lib/useThemeMode.js';
import { needsInit } from './MetricChart.js';
import { leafLabel } from '../money/format.js';

use([SankeyChart, TooltipComponent, CanvasRenderer]);

type ChartOption = ComposeOption<SankeySeriesOption | TooltipComponentOption>;

/** Whole dollars — a ribbon labelled to the cent is noise. */
function dollars(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString()}`;
}

/** Categories are tree paths (`lifestyle.dining`); a ribbon shows only the leaf ("Dining"). */
function catLabel(c: string): string {
  return leafLabel(c);
}

/**
 * Softer than the metric PALETTE on purpose (the Monarch look): the ribbons are
 * big areas, and saturated hues at that size read as harsh. Green is reserved
 * for money coming in or kept, so no category uses it.
 */
const FLOW_COLORS = {
  light: {
    green: '#3dbb8a', red: '#ec7f8c', neutral: '#c4c4cc',
    cats: ['#6f9ef5', '#f4a462', '#ec7f8c', '#a68ef0', '#55c2d8', '#f1c45a', '#e78fc9', '#8ea4c2', '#d6a679'],
    text: '#1c1c1e', muted: 'rgba(60,60,67,0.72)', ribbon: 0.32,
  },
  dark: {
    green: '#34a77b', red: '#d9707c', neutral: '#5c5c63',
    cats: ['#5b88dc', '#d98c4f', '#d9707c', '#8d76d9', '#46a8bd', '#d4a944', '#cc78b0', '#7489a6', '#b98d62'],
    text: '#f5f5f7', muted: 'rgba(235,235,245,0.7)', ribbon: 0.42,
  },
} as const;

const NODE_GAP = 14;

/** Rows a chart of this many nodes needs so the thinnest ribbon still fits a label. */
export function sankeyHeight(nodesPerSide: number): number {
  return Math.max(360, 520 + Math.max(0, nodesPerSide - 1) * NODE_GAP);
}

/**
 * What a tap on a node lists: rows filed under any of `categories` (and below),
 * or from any payer in `names`, limited to money in / out when `direction` says.
 */
export interface FlowDrill {
  title: string;
  categories?: string[];
  names?: string[];
  direction?: 'in' | 'out';
}

/** Every inflow the middle "Income" bar counts (same roots as the server's income side). */
const ALL_INCOME: FlowDrill = { title: 'Income', categories: ['income', 'business', 'uncategorized'], direction: 'in' };

interface SankeyNode {
  name: string;
  /** What a tap on this node (or a ribbon into it) opens; none for Saved / From savings. */
  drill?: FlowDrill;
  cents: number;
  color: string;
  side: 'left' | 'mid' | 'right';
}

/**
 * Nodes and links for the Monarch-style cash-flow picture: income sources on
 * the left, one "Income" bar in the middle, and on the right every spending
 * category plus what was kept. A month that spent more than came in gets a
 * "From savings" source on the left, so the ribbons still balance and the
 * shortfall is visible rather than silently dropped. Exported for tests.
 */
export function buildSankey(
  flow: CashFlow,
  mode: 'light' | 'dark',
  label: (category: string) => string = catLabel,
): { nodes: SankeyNode[]; links: Array<{ source: number; target: number; value: number }> } {
  const { green, red, neutral, cats } = FLOW_COLORS[mode];

  const nodes: SankeyNode[] = [];
  const links: Array<{ source: number; target: number; value: number }> = [];
  // ECharts keys sankey nodes by name, so a source and a category that share a
  // label ("Other") would merge; pad duplicates with zero-width spaces.
  const seen = new Set<string>();
  const add = (n: SankeyNode): number => {
    let name = n.name;
    while (seen.has(name)) name += '​';
    seen.add(name);
    nodes.push({ ...n, name });
    return nodes.length - 1;
  };

  // Labels sit inside the ribbons, so a sliver under 4% of the total can't
  // hold one. Fold them into one "Other" node per side.
  const total = Math.max(flow.incomeCents, flow.spentCents);
  // A filed income category (a job) always keeps its own node, however thin.
  // `other` gets the folded rows so the "Other" node can still list them.
  const fold = <T extends { cents: number; category?: string }>(list: T[], other: (cents: number, rest: T[]) => T): T[] => {
    const floor = total * 0.04;
    const pinned = (r: T) => r.category?.startsWith('income.') === true;
    const keep = list.filter((r) => r.cents >= floor || pinned(r));
    const rest = list.filter((r) => r.cents > 0 && r.cents < floor && !pinned(r));
    return rest.length > 0 ? [...keep, other(rest.reduce((sum, r) => sum + r.cents, 0), rest)] : keep;
  };
  // The server may already have sent an "Other" on either side; combine rather
  // than duplicate, keeping every payer / category either fold stood for.
  type Source = CashFlow['sources'][number];
  type Category = CashFlow['categories'][number];
  const payers = (r: Source): string[] => (r.name === 'Other income' ? (r.members ?? []) : [r.name]);
  const paths = (c: Category): string[] => (c.category === 'other' ? (c.members ?? []) : [c.category]);

  const foldedSources = fold(flow.sources, (cents, rest) => ({ name: 'Other income', cents, members: rest.flatMap(payers) }));
  const sources = foldedSources.filter((r) => r.name !== 'Other income');
  const otherIncome = foldedSources.filter((r) => r.name === 'Other income');
  const otherIncomeCents = otherIncome.reduce((sum, r) => sum + r.cents, 0);
  if (otherIncomeCents > 0) sources.push({ name: 'Other income', cents: otherIncomeCents, members: otherIncome.flatMap(payers) });

  const merged = fold(flow.categories, (cents, rest): Category => ({ category: 'other', cents, members: rest.flatMap(paths) }));
  const categories = merged.filter((c) => c.category !== 'other');
  const others = merged.filter((c) => c.category === 'other');
  const otherCents = others.reduce((sum, c) => sum + c.cents, 0);
  if (otherCents > 0) categories.push({ category: 'other', cents: otherCents, members: others.flatMap(paths) });

  const mid = add({ name: 'Income', cents: Math.max(flow.incomeCents, flow.spentCents), color: green, side: 'mid', drill: ALL_INCOME });

  for (const s of sources) {
    if (s.cents <= 0) continue;
    const drill: FlowDrill = s.category
      ? { title: s.name, categories: [s.category], direction: 'in' }
      : { title: s.name, names: s.members ?? [s.name], direction: 'in' };
    links.push({ source: add({ name: s.name, cents: s.cents, color: green, side: 'left', drill }), target: mid, value: s.cents });
  }
  if (flow.savedCents < 0) {
    const i = add({ name: 'From savings', cents: -flow.savedCents, color: red, side: 'left' });
    links.push({ source: i, target: mid, value: -flow.savedCents });
  }

  if (flow.savedCents > 0) {
    const i = add({ name: 'Saved', cents: flow.savedCents, color: green, side: 'right' });
    links.push({ source: mid, target: i, value: flow.savedCents });
  }
  categories.forEach((c, idx) => {
    if (c.cents <= 0) return;
    const color = c.category === 'other' || c.category === 'uncategorized' ? neutral : cats[idx % cats.length]!;
    const i = add({
      name: label(c.category),
      cents: c.cents,
      color,
      side: 'right',
      drill:
        c.category === 'other'
          ? { title: 'Other', categories: c.members ?? [] }
          : { title: label(c.category), categories: [c.category] },
    });
    links.push({ source: mid, target: i, value: c.cents });
  });

  return { nodes, links };
}

function buildOption(flow: CashFlow, mode: 'light' | 'dark', narrow: boolean, label?: (c: string) => string): ChartOption {
  const { nodes, links } = buildSankey(flow, mode, label);
  const { text, muted, ribbon } = FLOW_COLORS[mode];
  const total = Math.max(flow.incomeCents, flow.spentCents, 1);
  const max = narrow ? 13 : 24;

  return {
    tooltip: {
      trigger: 'item',
      formatter: (p: unknown) => {
        const d = (p as { dataType: string; data: { source?: number; target?: number; value?: number; name?: string; cents?: number } }).data;
        const pp = p as { dataType: string };
        if (pp.dataType === 'edge') {
          const from = nodes[d.source!]!.name;
          const to = nodes[d.target!]!.name;
          return `${from} → ${to}<br/><b>${dollars(d.value ?? 0)}</b>`;
        }
        return `${d.name}<br/><b>${dollars(d.cents ?? 0)}</b>`;
      },
    },
    series: [
      {
        type: 'sankey',
        // Labels live inside the ribbons, so the bars can run edge to edge.
        left: 2,
        right: 2,
        top: 30, // room for the "Income" label above the middle bar
        bottom: 4,
        nodeWidth: 8,
        nodeGap: NODE_GAP,
        nodeAlign: 'justify',
        layoutIterations: 0, // keep the server's order: biggest first, Saved on top
        draggable: false,
        emphasis: { focus: 'adjacency', lineStyle: { opacity: Math.min(0.75, ribbon + 0.3) } },
        data: nodes.map((n) => ({
          name: n.name,
          cents: n.cents,
          itemStyle: { color: n.color, borderWidth: 0, borderRadius: 4 },
          // Left labels read into the outgoing ribbon, right labels into the
          // incoming one; the middle bar's label sits on top of it.
          label: n.side === 'mid'
            ? { position: 'top', distance: 8, align: 'center' }
            : { position: n.side === 'left' ? 'right' : 'left', distance: 10 },
        })),
        links: links.map((l) => ({ ...l })),
        lineStyle: { color: 'gradient', opacity: ribbon, curveness: 0.5 },
        label: {
          color: text,
          formatter: (p: unknown) => {
            const d = (p as { data: { name: string; cents: number } }).data;
            const name = d.name.replace(/\u200b/g, '');
            const short = name.length > max ? `${name.slice(0, max - 1)}…` : name;
            const pct = Math.round((d.cents / total) * 100);
            return narrow
              ? `{n|${short}} {v|${dollars(d.cents)}}`
              : `{n|${short}}  {v|${dollars(d.cents)} · ${pct}%}`;
          },
          rich: {
            n: { color: text, fontSize: narrow ? 11 : 13, fontWeight: 600 },
            v: { color: muted, fontSize: narrow ? 10 : 12 },
          },
        },
      },
    ],
  };
}

/**
 * Cash flow as a Sankey diagram (the Monarch "Cash flow" view): where the money
 * came from, how much was kept, and where the rest went.
 */
export default function CashFlowSankey({
  flow,
  label,
  onDrill,
}: {
  flow: CashFlow;
  /** Path → display name (the tree's label); defaults to the path's leaf key. */
  label?: (category: string) => string;
  /** Any node with rows behind it (or its ribbon) was clicked. */
  onDrill?: (drill: FlowDrill) => void;
}): JSX.Element {
  const mode = useThemeMode();
  const elRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<ECharts | null>(null);
  const onDrillRef = useRef(onDrill);
  onDrillRef.current = onDrill;

  const rightCount = flow.categories.length + (flow.savedCents > 0 ? 1 : 0);
  const leftCount = flow.sources.length + (flow.savedCents < 0 ? 1 : 0);
  const height = sankeyHeight(Math.max(rightCount, leftCount));

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    if (needsInit(chartRef.current, el)) {
      chartRef.current?.dispose();
      chartRef.current = init(el);
    }
    const render = () => chartRef.current?.setOption(buildOption(flow, mode, el.clientWidth < 560, label), true);
    render();
    // Node names and link targets resolve back to the category behind them.
    const { nodes } = buildSankey(flow, mode, label);
    const chart = chartRef.current;
    chart?.off('click');
    chart?.on('click', (p) => {
      const data = p.data as { name?: string; target?: number | string } | undefined;
      const node =
        p.dataType === 'edge'
          ? typeof data?.target === 'number'
            ? nodes[data.target]
            : nodes.find((n) => n.name === data?.target)
          : nodes.find((n) => n.name === data?.name);
      if (node?.drill) onDrillRef.current?.(node.drill);
    });
    let narrow = el.clientWidth < 560;
    const ro = new ResizeObserver(() => {
      chartRef.current?.resize();
      // Re-lay out only when crossing the breakpoint, not on every pixel.
      if ((el.clientWidth < 560) !== narrow) {
        narrow = el.clientWidth < 560;
        render();
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [flow, mode, label]);

  useEffect(() => {
    return () => {
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, []);

  return (
    <div
      ref={elRef}
      className="mn-sankey"
      style={{ height }}
      role="img"
      aria-label={`Cash flow: ${dollars(flow.incomeCents)} in, ${dollars(flow.spentCents)} spent, ${
        flow.savedCents >= 0 ? `${dollars(flow.savedCents)} saved` : `${dollars(-flow.savedCents)} over`
      }`}
    />
  );
}
